<template>
  <div style="min-height: 100vh; background: #FFFFFF; font-family: 'IBM Plex Sans', sans-serif;">
    <!-- Header -->
    <div style="border-bottom: 1px solid #E8E9E9; position: sticky; top: 0; z-index: 40; background: #FFFFFF;">
      <button
        v-if="!sidebarOpen"
        @click="$emit('open-sidebar')"
        class="float-left relative group px-1.5 py-1 my-2 mx-3 rounded-md bg-primary-500 text-white text-sm hover:text-gray-600"
      >
        <font-awesome-icon :icon="['fas','chevron-right']" style="height: 12px; width: 12px"/>
        <div class="absolute left-1/2 -translate-x-1/2 -translate-y-[-10px] px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
          Open Sidebar
        </div>
      </button>
      <div style="padding: 12px 40px;">
        <h1 style="font-size: 14px; font-weight: 500; color: #1E1F21;">Attribution Roster</h1>
      </div>
    </div>

    <!-- Loading -->
    <LoadingIndicator v-if="loading" message="Loading attribution data..." />

    <!-- Error -->
    <div v-else-if="error" style="padding: 40px;">
      <div style="border: 1px solid #D53D3D; background: #FFF5F5; border-radius: 8px; padding: 16px;">
        <p style="color: #D53D3D; font-size: 14px;">{{ error }}</p>
      </div>
    </div>

    <!-- Content -->
    <div v-else style="padding: 20px 40px 40px;">

      <!-- Year Filter + Summary Row -->
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px;">
        <div v-if="availableYears.length" style="display: flex; align-items: center; gap: 8px;">
          <button v-for="year in availableYears" :key="year"
                  @click="selectedYear = year"
                  :style="{
                    padding: '6px 16px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: year === selectedYear ? '600' : '400',
                    border: year === selectedYear ? '1px solid #F76734' : '1px solid #E8E9E9',
                    background: year === selectedYear ? '#FFF7F4' : '#FFFFFF',
                    color: year === selectedYear ? '#F76734' : '#353537',
                    cursor: 'pointer',
                  }">
            PY {{ year }}
          </button>
        </div>
        <div style="display: flex; gap: 20px; font-size: 13px; color: #626264;">
          <span><strong style="color: #1E1F21;">{{ filtered.length }}</strong> contracts</span>
          <span><strong style="color: #1E1F21;">{{ formatNumber(totalPatients) }}</strong> patients</span>
        </div>
      </div>

      <!-- Single Table -->
      <div v-if="filtered.length" style="border: 1px solid #E8E9E9; border-radius: 8px; overflow: hidden;">
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr style="background: #FAFAFA;">
              <th v-for="col in columns" :key="col.key"
                  class="th-cell"
                  :class="{ num: col.align === 'right' }"
                  :style="{ cursor: 'pointer', userSelect: 'none', textAlign: col.align || 'left' }"
                  @click="toggleSort(col.key)">
                {{ col.label }}
                <span v-if="sortKey === col.key" style="margin-left: 4px; font-size: 10px;">
                  {{ sortDir === 'asc' ? '▲' : '▼' }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in sorted" :key="c.contract_id" class="table-row">
              <td class="td-cell">{{ c.payer || '—' }}</td>
              <td class="td-cell">{{ c.lob || '—' }}</td>
              <td class="td-cell">
                <span class="type-badge" :style="payerBadgeStyle(c.payer_type)">{{ c.payer_type }}</span>
              </td>
              <td class="td-cell" style="font-weight: 500;">{{ c.contract_id }}</td>
              <td class="td-cell">{{ c.contract_name }}</td>
              <td class="td-cell num">{{ formatNumber(c.patients) }}</td>
              <td class="td-cell num">{{ formatNumber(c.practice_count) }}</td>
              <td class="td-cell num">{{ formatNumber(c.tin_count) }}</td>
              <td class="td-cell" style="font-size: 12px; color: #626264;">{{ c.report_date || '—' }}</td>
              <td class="td-cell" style="text-align: center;">
                <span v-if="c.in_app" style="color: #389A6C;">Yes</span>
                <span v-else style="color: #D53D3D;">No</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-else style="text-align: center; padding: 40px 0;">
        <p style="font-size: 14px; color: #626264;">No attribution data for PY {{ selectedYear }}.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import axios from "axios";
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

export default {
  name: 'Attribution_Page',
  components: { FontAwesomeIcon, LoadingIndicator },
  props: {
    entityIds: {
      type: Array,
      required: false,
      default: () => []
    },
    entityId: {
      type: Number,
      default: null
    },
    sidebarOpen: {
      type: Boolean,
      default: true
    },
    year: { type: Number, default: null },
    metricId: { type: Number, default: null },
  },
  emits: ['open-sidebar'],
  data() {
    return {
      loading: false,
      error: null,
      contracts: [],
      selectedYear: null,
      sortKey: 'patients',
      sortDir: 'desc',
    };
  },
  computed: {
    columns() {
      return [
        { key: 'payer', label: 'Payer', align: 'left' },
        { key: 'lob', label: 'LOB', align: 'left' },
        { key: 'payer_type', label: 'Type', align: 'left' },
        { key: 'contract_id', label: 'Contract', align: 'left' },
        { key: 'contract_name', label: 'Name', align: 'left' },
        { key: 'patients', label: 'Patients', align: 'right' },
        { key: 'practice_count', label: 'Practices', align: 'right' },
        { key: 'tin_count', label: 'TINs', align: 'right' },
        { key: 'report_date', label: 'Report Date', align: 'left' },
        { key: 'in_app', label: 'In App', align: 'center' },
      ];
    },
    availableYears() {
      const years = new Set(this.contracts.map(c => c.performance_year).filter(Boolean));
      return [...years].sort((a, b) => b - a);
    },
    filtered() {
      if (!this.selectedYear) return [];
      return this.contracts.filter(c => c.performance_year === this.selectedYear);
    },
    sorted() {
      const rows = [...this.filtered];
      const key = this.sortKey;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      rows.sort((a, b) => {
        let va = a[key];
        let vb = b[key];
        if (va === null || va === undefined) va = '';
        if (vb === null || vb === undefined) vb = '';
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        if (typeof va === 'boolean' && typeof vb === 'boolean') return (va === vb ? 0 : va ? -1 : 1) * dir;
        return String(va).localeCompare(String(vb)) * dir;
      });
      return rows;
    },
    totalPatients() {
      return this.filtered.reduce((s, c) => s + (c.patients || 0), 0);
    },
  },
  watch: {
    contracts() {
      if (!this.selectedYear && this.availableYears.length) {
        this.selectedYear = this.availableYears[0];
      }
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const payload = {};
        if (this.entityId) {
          payload.entity_id = this.entityId;
        } else if (this.entityIds.length > 0) {
          payload.entity_id = this.entityIds[0];
        }
        const response = await axios.post('/api/portfolio_management/contract-manifest/', payload);
        this.contracts = response.data.contracts || [];
      } catch (err) {
        console.error('Error fetching contract manifest:', err);
        this.error = 'Failed to load attribution data.';
      } finally {
        this.loading = false;
      }
    },
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = key === 'patients' || key === 'practice_count' || key === 'tin_count' ? 'desc' : 'asc';
      }
    },
    payerBadgeStyle(type) {
      const styles = {
        DCE: 'background: #EBF4FF; color: #385883;',
        MSSP: 'background: #F1F9F0; color: #487546;',
        Private: 'background: #F4F4F4; color: #4B4C4D;',
      };
      return styles[type] || styles.Private;
    },
    formatNumber(val) {
      if (val === null || val === undefined) return '—';
      return Number(val).toLocaleString();
    },
  },
  mounted() {
    this.fetchData();
  },
}
</script>

<style scoped>
.th-cell {
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  color: #626264;
  text-align: left;
  white-space: nowrap;
}
.th-cell:hover {
  color: #1E1F21;
}
.td-cell {
  padding: 10px 16px;
  font-size: 13px;
  color: #353537;
}
.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.table-row {
  border-top: 1px solid #E8E9E9;
}
.table-row:hover {
  background: #FAFAFA;
}
.type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}
</style>
